import { api, asApiResponse, axiosErrorMessage, buildAuthApiUrl, buildUserApiUrl, withSkipAuth } from "./http";
import { decodeJwtUserId } from "./session";
import type { ApiResponse } from "./api-types";
import type { EnterpriseRoleEntry } from "./storage";

export type { EnterpriseRoleEntry };

export type { ApiResponse };

export type LoginPayload = { username: string; password: string };

// A user can hold a different role in every Enterprise they belong to (and,
// within one Enterprise, a different role per Department/Team) — this is
// the raw nested shape the login response carries that in.
export type LoginTeamRole = {
  teamId: string | null;
  teamName: string | null;
  roleId: string;
  roleName: string;
  profileId: string;
  profileName: string;
};

export type LoginDepartment = {
  departmentId: string | null;
  departmentName: string | null;
  teams: LoginTeamRole[];
};

export type LoginEnterprise = {
  enterpriseId: string;
  enterpriseName: string;
  departments: LoginDepartment[];
};

export type LoginResponse = {
  token: string;
  refreshToken?: string;
  userId?: string;
  user_id?: string;
  id?: string;
  enterprises?: LoginEnterprise[];
  [key: string]: unknown;
};

// Flattens the login response's nested enterprises[].departments[].teams[]
// structure into a single list, since a user can appear in multiple
// departments/teams (each with their own role) across multiple enterprises.
export function flattenEnterpriseRoles(enterprises?: LoginEnterprise[]): EnterpriseRoleEntry[] {
  if (!Array.isArray(enterprises)) return [];
  const out: EnterpriseRoleEntry[] = [];
  for (const ent of enterprises) {
    for (const dept of ent.departments ?? []) {
      for (const team of dept.teams ?? []) {
        out.push({
          enterpriseId: ent.enterpriseId,
          enterpriseName: ent.enterpriseName,
          departmentId: dept.departmentId ?? null,
          departmentName: dept.departmentName ?? null,
          teamId: team.teamId ?? null,
          teamName: team.teamName ?? null,
          roleId: team.roleId,
          roleName: team.roleName,
          profileId: team.profileId,
          profileName: team.profileName,
        });
      }
    }
  }
  return out;
}

export type EnterpriseRoleGroup = { enterpriseId: string; enterpriseName: string; roles: EnterpriseRoleEntry[] };

// Groups a flattened role list back by enterprise — used anywhere the UI
// shows "this enterprise -> these roles" (Profile screen's Roles &
// Enterprises card, the sidebar's account switcher).
export function groupEnterpriseRolesByEnterprise(entries: EnterpriseRoleEntry[]): EnterpriseRoleGroup[] {
  const order: string[] = [];
  const groups = new Map<string, EnterpriseRoleGroup>();
  for (const entry of entries) {
    if (!groups.has(entry.enterpriseId)) {
      groups.set(entry.enterpriseId, { enterpriseId: entry.enterpriseId, enterpriseName: entry.enterpriseName, roles: [] });
      order.push(entry.enterpriseId);
    }
    groups.get(entry.enterpriseId)!.roles.push(entry);
  }
  return order.map((id) => groups.get(id)!);
}

export type RegisterPayload = {
  name: string;
  mobileNumber?: string;
  email: string;
};

export type UpdateUserPayload = {
  id: string;
  name: string;
  mobileNumber: string;
  password: string;
  email: string;
  categoryId: string[];
  subcategoryId: string[];
  status: string;
  verified: boolean;
};

// Payload for the "update details" endpoint used by the Profile screen —
// distinct from UpdateUserPayload above (which is the full signup-completion
// payload with password/category fields).
export type UpdateProfileDetailsPayload = {
  name?: string;
  companyName?: string;
  businessType?: string;
  roleType?: string;
};

export const authApi = {
  login(payload: LoginPayload) {
    return asApiResponse<LoginResponse>(api.post(buildAuthApiUrl("/auth/login"), payload, withSkipAuth()));
  },

  getMe() {
    return asApiResponse<Record<string, unknown>>(api.get(buildAuthApiUrl("/auth/me")));
  },

  refresh(refreshToken: string) {
    return asApiResponse<LoginResponse>(
      api.post(buildAuthApiUrl("/auth/refresh"), undefined, withSkipAuth({ params: { refreshToken } })),
    );
  },
};

// GET /user — same User Service (:8084) as register/update above, ported
// from gully-properties-admin-portal-fe's lib/api/users.ts listUsers(), used
// to let an admin pick an already-registered user when inviting a team
// member instead of always typing a brand-new email.
export type AppUser = {
  id?: string;
  name: string;
  type?: "BUYER" | "SUPER_ADMIN" | "ADMIN" | "AGENT" | "SELLER";
  mobileNumber?: string;
  email: string;
  status?: string;
};

function asArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (Array.isArray(obj.content)) return obj.content as T[];
  }
  return [];
}

export const userApi = {
  register(payload: RegisterPayload) {
    return asApiResponse<Record<string, unknown>>(
      api.post(buildUserApiUrl("/user/register"), payload, withSkipAuth()),
    );
  },

  /** List registered users — used to populate the "invite member" combobox. */
  async list(params?: { name?: string; page?: number; size?: number }): Promise<ApiResponse<AppUser[]>> {
    const res = await asApiResponse<AppUser[] | { data?: AppUser[] }>(
      api.get(buildUserApiUrl("/user"), { params }),
    );
    if (!res.success) return res as ApiResponse<AppUser[]>;
    return { success: true, data: asArray<AppUser>(res.data), message: "Success" };
  },

  verifyOtp(mobileNumber: string, otp: string) {
    return asApiResponse<Record<string, unknown>>(
      api.post(buildUserApiUrl("/user/verify"), { mobileNumber, otp }, withSkipAuth()),
    );
  },

  /** PUT /{id}/password — same signup payload, new path after OTP. */
  update(userId: string, payload: UpdateUserPayload) {
    return asApiResponse<Record<string, unknown>>(
      api.put(buildUserApiUrl(`user/${userId}/password`), payload, withSkipAuth()),
    );
  },

  /** Profile screen — update the signed-in user's own details (authenticated). */
  updateDetails(userId: string, payload: UpdateProfileDetailsPayload) {
    return asApiResponse<Record<string, unknown>>(api.put(buildUserApiUrl(`/user/${userId}`), payload));
  },

  /** Profile screen — change password while signed in (authenticated). */
  updatePassword(userId: string, password: string) {
    return asApiResponse<Record<string, unknown>>(
      api.put(buildUserApiUrl(`/user/${userId}/password`), { password }),
    );
  },
};

// ===================== Passwordless first-login (invited user) flow =====================
// A user who was invited but has never set a password: enters email +
// mobile -> receives an OTP -> verifies it -> sets a password -> is logged
// in. All pre-authentication, so every call here explicitly skips auth
// (no token exists yet).
export const invitedAuthApi = {
  /** POST /user/invited — confirms an invited account exists for this email. */
  checkInvited(email: string) {
    return asApiResponse<Record<string, unknown>>(
      api.post(buildUserApiUrl("/user/invited"), { email }, withSkipAuth()),
    );
  },

  /** POST /user/invited/register — sends an OTP to the given mobile number. */
  requestOtp(email: string, mobileNumber: string) {
    return asApiResponse<Record<string, unknown>>(
      api.post(buildUserApiUrl("/user/invited/register"), { email, mobileNumber }, withSkipAuth()),
    );
  },

  /** POST /user/invited/verify — confirms the OTP; response carries the user id. */
  confirmOtp(email: string, mobileNumber: string, otp: string) {
    return asApiResponse<Record<string, unknown>>(
      api.post(buildUserApiUrl("/user/invited/verify"), { email, mobileNumber, otp }, withSkipAuth()),
    );
  },

  /** PUT /{id}/password — sets the password after OTP verification. */
  setPassword(userId: string, password: string) {
    return asApiResponse<Record<string, unknown>>(
      api.put(buildUserApiUrl(`user/${userId}/password`), { password }, withSkipAuth()),
    );
  },
};

export function extractUserId(data: Record<string, unknown> | undefined, token?: string): string {
  if (!data) return token ? decodeJwtUserId(token) || "" : "";
  const nested = data.data as Record<string, unknown> | undefined;
  const id =
    (data.id as string) ||
    (data.userId as string) ||
    (data.user_id as string) ||
    (nested?.id as string) ||
    (nested?.userId as string) ||
    "";
  if (id) return String(id);
  return token ? decodeJwtUserId(token) || "" : "";
}

export { axiosErrorMessage };
